import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import { LoadingContext } from "/src/helpers/contexts/agentContext";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router";
import formatMoney from "/src/helpers/formatMoney";
import { useApi } from "/src/helpers/Hooks/useApi";
import ExcursionShop from "/src/pages/excursionShop.jsx";
import BasketBar from "/src/helpers/components/basketBar.jsx";
import HomepageFlightRow from "/src/pages/HomepageFlightRow.jsx";
import HomepagePropertyRow from "/src/pages/HomepagePropertyRow.jsx";
import HomepageTransferRow from "/src/pages/HomepageTransferRow.jsx";
import HomepageAdhocRow from "/src/pages/HomepageAdhocRow.jsx";
import HomepageCarHireRow from "/src/pages/HomepageCarHireRow.jsx";
import HomepageExcursionRow from "/src/pages/HomepageExcursionRow.jsx";
import HelpSection from "/src/helpers/components/helpSection.jsx";
import IncentiveCard from "/src/helpers/components/incentiveCard.jsx";

// "Helpful information for your holiday" (contact, FAQs, transfers) is for the run-up to
// and during the trip: it appears from two weeks before departure until the customer is home.
// QA aid: `?help=show` / `?help=hide` on the URL overrides that on any booking.
export const HELP_SECTION_DAYS_BEFORE_DEPARTURE = 14;
export const showHelpSection = (model) => {
    const forced = new URLSearchParams(window.location.search).get("help");
    if (forced === "show") return true;
    if (forced === "hide") return false;
    return !model.hasReturned && typeof model.daysTillStart === "number"
        && model.daysTillStart <= HELP_SECTION_DAYS_BEFORE_DEPARTURE;
};
import Button from "/src/helpers/components/button.jsx";
import PlanBanner from "/src/helpers/components/planBanner.jsx";
import { track } from "/src/helpers/analytics";
import Toast, { useToast } from "/src/helpers/components/toast.jsx";
import { ChevronRightIcon, CheckIcon, CartIcon } from "/src/helpers/components/upsellIcons.jsx";

const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
const fmtTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (d.getHours() === 0 && d.getMinutes() === 0) return null;
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

// One compact row per leg for the collapsed itinerary (prototype MiniRow): icon tile,
// title + kicker sub-line, and times/date on the right.
const compactLeg = (item) => {
    switch (item.type) {
        case "flight":
            return {
                title: `${item.departureAirport} → ${item.arrivalAirport}`,
                sub: ["Flight", [item.airline, item.flightNumber].filter(Boolean).join(" ")].filter(Boolean).join(" · "),
                times: fmtTime(item.start) && fmtTime(item.end) ? `${fmtTime(item.start)} – ${fmtTime(item.end)}` : null,
                date: fmtDate(item.start),
                icon: "M6 19l3-7H4l-1 2H1l1.5-3L1 8h2l1 2h5L6 3h2l5 7h5a2 2 0 010 4h-5l-5 7H6z" };
        case "transfer":
            return {
                title: `${item.departureLocation} → ${item.arrivalLocation}`,
                sub: ["Transfer", item.vehicle].filter(Boolean).join(" · "),
                date: fmtDate(item.start),
                icon: "M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" };
        case "property":
            return {
                title: item.name,
                sub: ["Stay", item.resort].filter(Boolean).join(" · "),
                dateRange: [fmtDate(item.start), fmtDate(item.end)],
                icon: "M3 21V8l9-5 9 5v13M9 21v-6h6v6M3 21h18" };
        case "carhire":
            return {
                title: item.vehicleDescription, sub: "Car hire",
                dateRange: [fmtDate(item.start), fmtDate(item.end)],
                icon: "M3 13l2-5a2 2 0 011.9-1.4h10.2A2 2 0 0119 8l2 5m-18 0h18m-18 0v4a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-4" };
        case "excursion":
            return {
                title: item.name, sub: "Excursion", date: fmtDate(item.start),
                icon: "M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" };
        case "adhoc":
            return {
                title: item.description, sub: "Extra", date: fmtDate(item.start),
                icon: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" };
        default:
            return null;
    }
};

const fullLeg = (item) => {
    switch (item.type) {
        case "flight": return <HomepageFlightRow item={item} />;
        case "property": return <HomepagePropertyRow item={item} />;
        case "transfer": return <HomepageTransferRow item={item} />;
        case "adhoc": return <HomepageAdhocRow item={item} />;
        case "carhire": return <HomepageCarHireRow item={item} />;
        case "excursion": return <HomepageExcursionRow item={item} />;
        default: return null;
    }
};

// Orange calendar-check tile + heading above the upsell banners (prototype PlanSectionHeading).
const PlanSectionHeading = () => (
    <div className="flex items-center gap-4 mb-[1.125rem]">
        <div className="w-[2.875rem] h-[2.875rem] rounded-[0.8125rem] bg-[#fdeccf] flex items-center justify-center shrink-0">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c47a12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="M8.5 15.5l2 2 4-4" />
            </svg>
        </div>
        <h2 className="text-[1.625rem] font-poppins font-bold text-primary">What's next to plan?</h2>
    </div>
);

const HomePage = () => {
    const { model } = useOutletContext();
    const api = useApi();
    const location = useLocation();
    const navigate = useNavigate();
    const [itineraryExpanded, setItineraryExpanded] = useState(false);
    const { toast, flash } = useToast();

    // Excursion shop data + basket load before the page is shown, so the "What's next to plan?"
    // banners don't jump in underneath a customer who has already started reading.
    // The excursion shop is revealed inline, so its open state lives in the URL (?excursions=1):
    // opening pushes a history entry and the browser's Back closes it again instead of leaving
    // the page (previously Back went straight to the login page).
    const excursionsOpen = new URLSearchParams(location.search).get("excursions") === "1";
    // Set by the login page when the customer signed in with their extras (ancillary) reference and
    // was switched to this, their main booking. Shown once; dismissing also clears the router state
    // so a refresh doesn't bring it back.
    const [redirectedFrom, setRedirectedFrom] = useState(location.state?.redirectedFrom || null);
    const dismissRedirectNotice = () => {
        setRedirectedFrom(null);
        navigate(location.pathname, { replace: true, state: {} });
    };
    const [excursionEligibility, setExcursionEligibility] = useState(null);
    const [excursionRows, setExcursionRows] = useState(null);
    const [basket, setBasket] = useState(null);
    const bannerRef = useRef(null);
    const excursionSectionRef = useRef(null);

    useEffect(() => {
        (async () => {
            const basketRes = await api.basketGet();
            setBasket(basketRes?.ok ? basketRes.data : { excursions: [], car: null }); // never block the page on the basket
            if (!model?.excursionAddMode) { setExcursionRows([]); return; }
            const elig = await api.excursionEligibility();
            setExcursionEligibility(elig);
            if (!elig?.canAdd) { setExcursionRows([]); return; }
            const rows = await api.excursionSearch(true); // silent: the page shows its own loading state
            setExcursionRows(rows || []);
        })();
    }, []);
    // Which upsell banners the customer was shown (GA4 view_promotion → promotion funnel).
    const bannersShown = `${excursionRows?.length ? "excursions" : ""}|${model?.carHireAddMode ? "car_hire" : ""}`;
    useEffect(() => {
        if (excursionRows === null) return;
        if (excursionRows.length) track("view_promotion", { promotion_id: "excursions", promotion_name: "Book an excursion" });
        if (model?.carHireAddMode) track("view_promotion", { promotion_id: "car_hire", promotion_name: "Add car hire" });
    }, [bannersShown]);

    // Both the basket and the excursion lookup have settled (rows is [] when there's nothing to sell).
    // Until then the site's standard loading overlay covers the (empty) page.
    const ready = excursionRows !== null && basket !== null;
    // Layout effect: the wrapper's getBooking() has just dropped the overlay in the same commit
    // this page mounts in, so raise it again before the browser paints (no flash between the
    // two) and keep the same message so it reads as one continuous load.
    const [, setLoading] = useContext(LoadingContext);
    useLayoutEffect(() => {
        setLoading(!ready, "Loading booking...");
        return () => setLoading(false, "");
    }, [ready]);

    const showExcursions = !!model?.excursionAddMode && (excursionRows?.length || 0) > 0;

    // Arriving with ?excursions=1 (checkout's "Browse excursions") the section only exists
    // once the rows have loaded, so re-run when it appears — otherwise the scroll is a no-op and
    // the link looks identical to "Back to My Booking".
    useEffect(() => {
        if (excursionsOpen && showExcursions)
            excursionSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [excursionsOpen, showExcursions]);

    const toggleExcursions = () => {
        const next = !excursionsOpen;
        track(next ? "select_promotion" : "upsell_banner_close", { promotion_id: "excursions", promotion_name: "Book an excursion" });
        const params = new URLSearchParams(location.search);
        if (next) {
            params.set("excursions", "1");
            navigate({ pathname: location.pathname, search: `?${params}` }, { state: { excursionsPushed: true } });
            setTimeout(() => bannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
        } else if (location.state?.excursionsPushed) {
            navigate(-1); // the entry the banner pushed — same as the browser's Back
        } else {
            params.delete("excursions");
            navigate({ pathname: location.pathname, search: params.size ? `?${params}` : "" }, { replace: true });
        }
    };

    const fromPp = (excursionRows || [])
        .map((r) => r.perPersonPrice)
        .filter((p) => p > 0)
        .reduce((min, p) => (min === null || p < min ? p : min), null);

    const showOutstandingBalance = model?.totalOutstanding > 0 && model?.authenticationLevel === "DirectByPax";
    // On an instalments ("pay monthly") plan, lead with the next payment rather than the final due date.
    const scheduledPayments = model?.scheduledPayments || [];
    const nextPayment = scheduledPayments.length > 1 ? scheduledPayments[0] : null;
    // The excursions banner only appears once the background search has found something to sell
    // (Olympic: "if no excursions are available don't show the widget").
    const ancillary = model?.ancillary;

    if (!ready) return null;

    return (
        <div>
            {/* ── Countdown banner: single line, no subtitle (prototype chrome) ───────────── */}
            <div className="relative rounded-card p-6 md:px-10 md:py-8 mb-6 text-white overflow-hidden
                            bg-[linear-gradient(95deg,#1a8fd9_0%,#0e7fc7_60%,#0d68a8_100%)]">
                <div className="absolute -top-[3.75rem] -right-10 w-[12.5rem] h-[12.5rem] bg-white/[0.06] rounded-full" />
                <div className="absolute right-20 -bottom-20 w-40 h-40 bg-white/5 rounded-full" />

                <p className="text-sm text-white/[0.92] mb-3.5 relative z-10">Booking reference: <span className="font-semibold text-white">{model.bookingReference}</span></p>
                {model.status === "Closed" ? (
                    <h1 className="text-2xl md:text-3xl font-poppins font-semibold tracking-[-0.01em] relative z-10">We hope you had a wonderful holiday, {model.customerDetails?.firstName}!</h1>
                ) : model.status === "Cancelled" ? (
                    <h1 className="text-2xl md:text-3xl font-poppins font-semibold tracking-[-0.01em] relative z-10">This booking has been cancelled, {model.customerDetails?.firstName}.</h1>
                ) : model.daysTillStart <= 0 ? (
                    <h1 className="text-2xl md:text-3xl font-poppins font-semibold tracking-[-0.01em] relative z-10">Enjoy your holiday, {model.customerDetails?.firstName}! You can still add excursions and car hire below.</h1>
                ) : (
                    <h1 className="text-2xl md:text-3xl font-poppins font-semibold tracking-[-0.01em] relative z-10">The countdown is on {model.customerDetails?.firstName}, {model.daysTillStart} day{model.daysTillStart !== 1 ? "s" : ""} until you will be going on holiday!</h1>
                )}
            </div>

            {/* ── Signed in with the extras reference: explain the switch to the main booking ── */}
            {redirectedFrom && (
                <div role="status" className="rounded-2xl px-[1.625rem] py-[1.125rem] mb-6 border border-emp/30 bg-emp_soft flex items-start gap-3">
                    <div className="text-emp_dark shrink-0 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-[1.375rem] w-[1.375rem]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p className="flex-1 text-sm md:text-base text-primary leading-normal">
                        <span className="font-semibold">You signed in with your extras reference {redirectedFrom}.</span>{" "}
                        Your car hire and excursions are managed from your main holiday booking {model.bookingReference}, so we've opened that instead.
                    </p>
                    <button type="button" onClick={dismissRedirectNotice} aria-label="Dismiss"
                            className="shrink-0 -mr-2 -mt-1 p-2 rounded-lg text-content_2 hover:text-primary hover:bg-white/60 transition cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            {/* ── Itinerary: header + compact rows live inside the card (prototype §2.4) ──── */}
            <section className="mb-6">
                {!itineraryExpanded ? (
                    <div className="bg-white rounded-card shadow-card px-8 py-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                            <h2 className="text-[1.375rem] font-poppins font-bold text-primary">Here's your itinerary at a glance</h2>
                            <Button variant="primary" className="md:w-[14.375rem]" onClick={() => setItineraryExpanded(true)}>
                                Show full itinerary
                                <ChevronRightIcon size={16} className="ml-1.5 rotate-90" />
                            </Button>
                        </div>
                        <div>
                            {model.components.map((item, index) => {
                                const leg = compactLeg(item);
                                if (!leg) return null;
                                return (
                                    <div key={index} className="flex items-center gap-3 sm:gap-4 py-2">
                                        <div className="w-[2.375rem] h-[2.375rem] rounded-[0.625rem] bg-emp_soft flex items-center justify-center shrink-0">
                                            <svg className="h-5 w-5 text-emp_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d={leg.icon} />
                                            </svg>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {/* Phones: the name may take two lines (the stacked date column is two
                                                lines tall anyway); from sm up it stays a single truncated line. */}
                                            <p className="text-sm font-semibold text-primary leading-snug line-clamp-2 sm:leading-normal sm:line-clamp-none sm:truncate">{leg.title}</p>
                                            <p className="text-[0.8125rem] text-content_2 truncate">{leg.sub}</p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            {leg.times && <p className="text-sm font-semibold text-primary">{leg.times}</p>}
                                            {leg.dateRange ? (
                                                // Stay / car-hire periods stack on phones so the name column keeps
                                                // its width; one line from sm up where there's room.
                                                <p className="text-xs sm:text-[0.8125rem] text-content_2 leading-snug">
                                                    <span className="block sm:inline">{leg.dateRange[0]}</span>
                                                    <span className="block sm:inline"> – {leg.dateRange[1]}</span>
                                                </p>
                                            ) : (
                                                <p className="text-xs sm:text-[0.8125rem] text-content_2">{leg.date}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-card shadow-card px-8 py-6 mb-[1.125rem]">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                <h2 className="text-[1.375rem] font-poppins font-bold text-primary">Here's your itinerary at a glance</h2>
                                <Button variant="primary" className="md:w-[14.375rem]" onClick={() => setItineraryExpanded(false)}>
                                    Hide full itinerary
                                    <ChevronRightIcon size={16} className="ml-1.5 -rotate-90" />
                                </Button>
                            </div>
                        </div>
                        {/* Each leg in its own separate card on the blue background — no connecting rail. */}
                        <div className="flex flex-col gap-[1.125rem]">
                            {model.components.map((item, index) => (
                                <div key={index}>{fullLeg(item)}</div>
                            ))}
                        </div>
                        <div className="flex justify-center mt-[1.125rem]">
                            <Button variant="primary" className="md:w-[14.375rem]" onClick={() => setItineraryExpanded(false)}>
                                Hide full itinerary
                                <ChevronRightIcon size={16} className="ml-1.5 -rotate-90" />
                            </Button>
                        </div>
                    </>
                )}
            </section>

            {/* ── Linked extras booking (customer purchases live on a separate, 1:1 booking) ── */}
            {ancillary && (
                <section className="mb-6 bg-white rounded-card shadow-card px-8 py-5">
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                        <div className="w-[2.375rem] h-[2.375rem] rounded-[0.625rem] bg-emp_soft text-emp_dark flex items-center justify-center shrink-0">
                            <CartIcon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg font-poppins font-bold text-primary leading-tight">
                                Your extras booking <span className="text-emp_dark">{ancillary.reference}</span>
                            </h2>
                            <p className="text-[0.84375rem] text-content_2 mt-0.5">
                                {ancillary.componentCount} {ancillary.componentCount === 1 ? "extra" : "extras"} linked to this holiday
                                — shown in your itinerary above{ancillary.status && ancillary.status !== "Live" ? ` · ${ancillary.status}` : ""}.
                            </p>
                        </div>
                        <div className="shrink-0 text-left md:text-right">
                            <div className="text-xl font-bold text-primary">£{formatMoney(ancillary.totalPrice)}</div>
                            {ancillary.totalOutstanding > 0 ? (
                                <div className="text-[0.8125rem] text-orange font-semibold">£{formatMoney(ancillary.totalOutstanding)} outstanding</div>
                            ) : (
                                <div className="inline-flex items-center gap-1 text-[0.8125rem] text-green font-semibold">
                                    <CheckIcon size={12} /> Paid in full by card
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Agent incentive: only for the travel agent who placed the booking ────────── */}
            {model.authenticationLevel === "TradeBookingByAgent" && <IncentiveCard />}

            {/* ── "What's next to plan?" + upsell banners (prototype §2.5–2.7) ────────────── */}
            {(showExcursions || model.carHireAddMode) && <PlanSectionHeading />}

            {showExcursions && (
                <div ref={bannerRef} className="mb-6 scroll-mt-20">
                    <PlanBanner
                        kind="excursion"
                        img={(excursionRows || []).find((r) => r.imageUrl)?.imageUrl || "/assets/upsell/excursions-banner.jpg"}
                        title="Book an excursion"
                        desc="From guided tours to unforgettable experiences, explore more of your destination."
                        price={fromPp ? `£${formatMoney(fromPp)}` : null}
                        priceUnit="per adult"
                        cta="Find excursions"
                        expandedLabel="Hide excursions"
                        expanded={excursionsOpen}
                        onClick={toggleExcursions}
                    />
                </div>
            )}

            {/* ── Excursions section, revealed inline by the banner ───────────────────────── */}
            {showExcursions && excursionsOpen && (
                <section ref={excursionSectionRef} className="mb-6 scroll-mt-20">
                    <ExcursionShop
                        eligibility={excursionEligibility}
                        rows={excursionRows}
                        basket={basket}
                        onBasketChange={setBasket}
                        onToast={flash}
                    />
                </section>
            )}

            {model.carHireAddMode && (
                <div className="mb-6">
                    <PlanBanner
                        kind="car"
                        img="/assets/upsell/carhire-banner.jpg"
                        title="Add car hire"
                        desc="Explore at your own pace with the freedom of your own car."
                        cta="Find cars"
                        onClick={() => { track("select_promotion", { promotion_id: "car_hire", promotion_name: "Add car hire" }); navigate("/carhire"); }}
                    />
                </div>
            )}

            {/* ── Make a Payment: bottom of the upsell content, pink (guide §2.8) ─────────── */}
            {showOutstandingBalance && (
                <div className="rounded-2xl px-[1.625rem] py-[1.125rem] mb-6 border border-[#f6c5d4] bg-[#fdeef2]">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="text-payment">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-[1.625rem] w-[1.625rem]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            {nextPayment ? (
                                <p className="text-base text-primary">
                                    Your next payment of <span className="font-semibold">£{formatMoney(nextPayment.amount)}</span> is due on <span className="font-semibold">{new Date(nextPayment.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                                    <span className="block text-sm text-content_2">£{formatMoney(model.totalOutstanding)} outstanding in total</span>
                                </p>
                            ) : (
                                <p className="text-base text-primary">
                                    <span className="font-semibold">£{formatMoney(model.totalOutstanding)}</span> outstanding balance is due by <span className="font-semibold">{model.balanceDueDate}</span>
                                </p>
                            )}
                        </div>
                        <Link to="/payment" className="shrink-0 w-full md:w-auto">
                            <Button variant="payment">Make a Payment</Button>
                        </Link>
                    </div>
                </div>
            )}

            {/* ── Help (guide §2.9) — from two weeks before departure until they're home ── */}
            {showHelpSection(model) && (
                <HelpSection showTransfers={model.components.some((c) => c.type === "transfer" && c.cancelled !== true)}
                             productType={model.productType} />
            )}

            <BasketBar basket={basket} />
            <Toast message={toast} />
         </div>
    );
};

export default HomePage;
